import { Table, Typography, Tooltip } from '@statsbomb/kitbag-components';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { SpokeIcon } from '@/components/icons/SpokeIcon';
import { MetricTooltip } from '@/components/metricTooltip/MetricTooltip';
import { CellValueToDisplay } from '@/components/tables/CellValueToDisplay';
import { METRIC_KEY, PITCH_PREVIEW_TAB } from '@/consts/searchKeys';
import { useUpdateQueryString } from '@/hooks/useUpdateQueryString';
import { PreviewTab } from '@/types/tabs';
import { snakeToCamel } from '@/utils/queries';

// `content` overrides the tooltip text; it defaults to the 360-unavailable message so existing
// call sites are unchanged, while the no-data case can pass its own explanation.
const RadarTableNaCell = ({ placement, content }: { placement?: string; content?: string }) => {
  const { t } = useTranslation(['table', 'dataVis']);
  return (
    <Tooltip placement={placement} content={content ?? t('threeSixtyUnavailableInfoBar', { ns: 'dataVis' })}>
      <span>{t('na', { ns: 'table' })}</span>
    </Tooltip>
  );
};

export type RadarTableRowProps = {
  metricKey: string;
  value: number;
  decimalPlaces: number;
  index: number;
  totalRows: number;
  showMetricName: boolean;
  isInteractive: boolean;
  percentile: number | undefined;
  hasValue: boolean;
  is360Unavailable: boolean;
  displayPlaceholderValue: boolean;
  showAsteriskWithTooltip: boolean;
  tooltipContent: string | undefined;
  hasComparisonData: boolean;
  comparisonPercentile: number | undefined;
  comparisonValue: number | undefined;
  comparisonHasValue: boolean | undefined;
  isComparison360Unavailable: boolean;
  comparisonShowAsterisk: boolean;
  comparisonTooltipContent: string | undefined;
  formatNumberDecimals: (decimalPlaces: number, value: number) => string;
  showSpokes?: boolean;
};

export const RadarTableRow = ({
  metricKey,
  value,
  decimalPlaces,
  index,
  totalRows,
  showMetricName,
  isInteractive,
  percentile,
  hasValue,
  is360Unavailable,
  displayPlaceholderValue,
  showAsteriskWithTooltip,
  tooltipContent,
  hasComparisonData,
  comparisonPercentile,
  comparisonValue,
  comparisonHasValue,
  isComparison360Unavailable,
  comparisonShowAsterisk,
  comparisonTooltipContent,
  formatNumberDecimals,
  showSpokes = true,
}: RadarTableRowProps) => {
  const { t } = useTranslation('table');
  const { t: tMetrics } = useTranslation('metrics');
  const [searchParams] = useSearchParams();
  const createQueryString = useUpdateQueryString(searchParams);
  const getPreviewParams = (tab: PreviewTab) =>
    isInteractive
      ? createQueryString([
          { key: METRIC_KEY, value: metricKey },
          { key: PITCH_PREVIEW_TAB, value: tab },
        ])
      : undefined;
  const primaryPreviewParams = getPreviewParams(PreviewTab.PRIMARY);
  const comparisonPreviewParams = getPreviewParams(PreviewTab.COMPARISON);
  const noDataContent = t('noDataForMetric');
  const displayDataPill = !displayPlaceholderValue && !is360Unavailable && hasValue && percentile !== undefined;
  const dataPill = displayDataPill ? { icon: 'circle' as const, percentile } : undefined;

  const comparisonDataPill =
    !displayPlaceholderValue && !isComparison360Unavailable && comparisonHasValue && comparisonPercentile !== undefined
      ? { icon: 'circle' as const, percentile: comparisonPercentile }
      : undefined;

  const getValueCell = () => {
    if (displayPlaceholderValue) return '-';
    if (is360Unavailable) return <RadarTableNaCell placement="top-centre" />;
    if (!hasValue) return <RadarTableNaCell placement="top-centre" content={noDataContent} />;
    return formatNumberDecimals(decimalPlaces, value);
  };

  const getPercentileCell = () => {
    if (displayPlaceholderValue) return '-';
    if (is360Unavailable) return <RadarTableNaCell placement="top-centre" />;
    if (!hasValue) return <RadarTableNaCell placement="top-centre" content={noDataContent} />;
    if (percentile === undefined) return '-';
    return percentile;
  };

  const getComparisonValueCell = () => {
    if (!hasComparisonData || displayPlaceholderValue || comparisonValue === undefined) return '';
    if (isComparison360Unavailable) return <RadarTableNaCell placement="left-centre" />;
    if (!comparisonHasValue) return <RadarTableNaCell placement="left-centre" content={noDataContent} />;
    return formatNumberDecimals(decimalPlaces, comparisonValue);
  };

  const getComparisonPercentileCell = () => {
    if (!hasComparisonData || displayPlaceholderValue) return '';
    if (isComparison360Unavailable) return <RadarTableNaCell placement="left-centre" />;
    if (!comparisonHasValue) return <RadarTableNaCell placement="left-centre" content={noDataContent} />;
    if (comparisonPercentile === undefined) return '';
    return comparisonPercentile;
  };

  return (
    <Table.Row>
      {showSpokes && (
        <Table.DataCell>
          <SpokeIcon numberOfSpokes={totalRows} spokeIndex={index} />
        </Table.DataCell>
      )}
      <Table.DataCell textAlign="right">
        {!showMetricName ? (
          '-'
        ) : (
          <MetricTooltip metricKey={metricKey}>
            <Typography as="span" variant="headingSmall">
              {tMetrics(`${snakeToCamel(metricKey)}.name`)}
            </Typography>
          </MetricTooltip>
        )}
      </Table.DataCell>
      {displayPlaceholderValue || is360Unavailable || !hasValue ? (
        <Table.DataCell textAlign="right">
          <Typography variant="bodyMedium" as="span">
            {getValueCell()}
          </Typography>
        </Table.DataCell>
      ) : (
        <CellValueToDisplay
          columnKey={metricKey}
          formatRule="number"
          key={`${metricKey}-value`}
          previewLinkParams={primaryPreviewParams}
          showAsteriskWithTooltip={showAsteriskWithTooltip}
          showNullAsNa={false}
          testId={`${metricKey}-value`}
          tooltipContent={tooltipContent}
          value={formatNumberDecimals(decimalPlaces, value)}
        />
      )}
      <Table.DataCell textAlign="right" dataPill={dataPill}>
        <Typography variant="bodyMedium" as="span">
          {getPercentileCell()}
        </Typography>
      </Table.DataCell>
      {hasComparisonData && (
        <>
          {displayPlaceholderValue ||
          isComparison360Unavailable ||
          comparisonValue === undefined ||
          !comparisonHasValue ? (
            <Table.DataCell textAlign="right" className="border-l border-canvas-primary-main">
              <Typography variant="bodyMedium" as="span">
                {getComparisonValueCell()}
              </Typography>
            </Table.DataCell>
          ) : (
            <CellValueToDisplay
              columnKey={metricKey}
              formatRule="number"
              key={`${metricKey}-comparison-value`}
              previewLinkParams={comparisonPreviewParams}
              showAsteriskWithTooltip={comparisonShowAsterisk}
              showNullAsNa={false}
              testId={`${metricKey}-comparison-value`}
              tooltipContent={comparisonTooltipContent}
              value={formatNumberDecimals(decimalPlaces, comparisonValue)}
              className="border-l border-canvas-primary-main"
            />
          )}
          <Table.DataCell textAlign="right" dataPill={comparisonDataPill}>
            <Typography variant="bodyMedium" as="span">
              {getComparisonPercentileCell()}
            </Typography>
          </Table.DataCell>
        </>
      )}
    </Table.Row>
  );
};
